import { type Location } from "@/gql/localities";
import { Bounds } from "@/types/map";
import { LocationType, type Filters } from "@/types/filters";

type LocationBase = {
	id: string;
	name: string;
	type: LocationType;
};

export interface LocationWithGps {
	id: string;
	name: string;
	type: LocationType;
	gps?: { lat: number; lng: number } | null;
}

// Define a type that makes bounds optional in the base Filters
type FiltersWithOptionalBounds = Omit<Filters, "bounds"> & {
	bounds?: Bounds[];
};

export interface EnhancedFilters extends Omit<Partial<FiltersWithOptionalBounds>, "locations"> {
	locations?: LocationBase[];
	locationsWithGps?: LocationWithGps[];
	centroid?: { lat: number; lng: number } | null;
	matchedServerLocations?: Location[];
}

interface CalculateBoundsParams {
	center: { lat: number; lng: number };
	currentBounds?: Bounds;
	currentZoom?: number;
	newZoom?: number;
}

/**
 * Calculate bounds from a center point, optionally adjusting for zoom level changes
 * @param params - Destructured parameters object
 * @param params.center - Center point for the new bounds
 * @param params.currentBounds - Current map bounds (optional)
 * @param params.currentZoom - Current zoom level (optional)
 * @param params.newZoom - Target zoom level (optional)
 * @returns New bounds centered on the given point
 */
export function calculateBounds({ center, currentBounds, currentZoom, newZoom }: CalculateBoundsParams): Bounds {
	// Calculate zoom scaling factor if both zoom levels are provided
	const zoomScaleFactor =
		currentZoom !== undefined && newZoom !== undefined
			? Math.pow(2, currentZoom - newZoom) // Each zoom level doubles/halves the scale
			: 1;

	// If no current bounds provided, or invalid point bounds, use default km values as fallback
	if (!currentBounds || currentBounds.north === currentBounds.south) {
		const R = 6371; // Earth's radius in km
		const lat = center.lat * (Math.PI / 180);

		// Apply zoom scaling to default dimensions
		const defaultTopBottomKm = 4 * zoomScaleFactor;
		const defaultSideKm = 2 * zoomScaleFactor;

		const dLatTop = (defaultTopBottomKm / R) * (180 / Math.PI);
		const dLatBottom = (defaultTopBottomKm / R) * (180 / Math.PI);
		const dLng = (defaultSideKm / (R * Math.cos(lat))) * (180 / Math.PI);

		return {
			north: center.lat + dLatTop,
			south: center.lat - dLatBottom,
			east: center.lng + dLng,
			west: center.lng - dLng,
		};
	}

	// Calculate the current bounds dimensions and apply zoom scaling
	const latitudeSpan = (currentBounds.north - currentBounds.south) * zoomScaleFactor;
	const longitudeSpan = (currentBounds.east - currentBounds.west) * zoomScaleFactor;

	// Calculate half spans to center around the new point
	const halfLatSpan = latitudeSpan / 2;
	const halfLngSpan = longitudeSpan / 2;

	return {
		north: center.lat + halfLatSpan,
		south: center.lat - halfLatSpan,
		east: center.lng + halfLngSpan,
		west: center.lng - halfLngSpan,
	};
}
